// Optional "override the institution's academic config" section for the School
// create/edit forms (PRD-38 §FR-11 / ADR-0032).
//
// Default state is INHERIT for every field, and each field is independent: an
// admin can override only `grade_scale` and leave the period calendar
// inherited. Every "inherit" choice names the value it would inherit, so the
// section reads without cross-referencing the institution page.
//
// `periods_per_cycle` counts the periods of the WHOLE PROGRAM (PRD-38 §FR-8),
// so a campus overrides it when its program length differs — e.g. a prepa
// annex (6 semestres) under an institution configured for secundaria.
//
// PRD-60 chunk B: a disclosure, closed while the campus inherits everything
// — most never touch it, and it doubled the form's height. All three are
// selects now; the count used to be a number input whose "inherit" state was
// an empty box with the inherited value as placeholder text.
//
// Wire semantics live in `@lib/academicConfig::toAcademicOverridePayload`:
// clearing a field back to inherit sends an EXPLICIT null, never an omitted
// key — the backend's `exclude_unset=True` treats those two very differently.

import { useAcademicPeriodsError } from "@components/AcademicConfigFields"
import { Field, SelectInput } from "@components/ui/Field"
import { useInstitutions } from "@features/institutions"
import type { AcademicOverrideValues } from "@lib/academicConfig"
import { ChevronDown } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  GRADE_SCALES,
  MAX_PERIODS_PER_CYCLE,
  MIN_PERIODS_PER_CYCLE,
  PERIOD_TYPES,
  type GradeScale,
  type PeriodType,
} from "@/constants/academic"

const PERIOD_COUNTS = Array.from(
  { length: MAX_PERIODS_PER_CYCLE - MIN_PERIODS_PER_CYCLE + 1 },
  (_, i) => MIN_PERIODS_PER_CYCLE + i,
)

interface Props {
  /** Institution whose config is inherited. */
  institutionId: string
  values: AcademicOverrideValues
  onChange: (patch: Partial<AcademicOverrideValues>) => void
  errorCode: string | undefined
  /** Open on arrival when this campus already overrides something. */
  defaultOpen?: boolean
}

export function AcademicOverrideFields({
  institutionId,
  values,
  onChange,
  errorCode,
  defaultOpen,
}: Props) {
  const { t } = useTranslation(["schools", "common"])
  const { data: institutions } = useInstitutions()
  const periodsError = useAcademicPeriodsError(errorCode)

  const parent = institutions?.find((inst) => inst.id === institutionId)
  const overridden =
    values.period_type !== null ||
    (values.periods_per_cycle !== null && !Number.isNaN(values.periods_per_cycle)) ||
    values.grade_scale !== null

  // Falls back to a bare "inherit" while the institution list is in flight,
  // so an option is never blank.
  const inheritLabel = (value: string | number | undefined) =>
    value === undefined
      ? t("common:academic.inherit")
      : t("common:academic.inherit_value", { value })

  return (
    <details open={defaultOpen || !!periodsError} className="group border-hairline border-t pt-4">
      <summary className="rounded-control -mx-1 flex cursor-pointer list-none items-start justify-between gap-3 px-1 py-1 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">
          <span className="text-ink block text-sm font-semibold">
            {t("academic.override.title")}
          </span>
          <span className="text-meta block">
            {overridden
              ? t("academic.override.summary_custom", { institution: parent?.name ?? "" })
              : t("academic.override.summary_inherit", { institution: parent?.name ?? "" })}
          </span>
        </span>
        <ChevronDown
          className="text-ink-subtle mt-0.5 h-5 w-5 shrink-0 transition-transform duration-150 group-open:rotate-180"
          aria-hidden
        />
      </summary>

      <div className="mt-4 space-y-4">
        <p className="text-ink-muted text-sm">{t("academic.override.help")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("common:academic.period_type.label")}>
            {({ id }) => (
              <SelectInput
                id={id}
                value={values.period_type ?? ""}
                onChange={(e) =>
                  onChange({ period_type: (e.target.value || null) as PeriodType | null })
                }
              >
                <option value="">
                  {inheritLabel(parent && t(`common:academic.period_type.${parent.period_type}`))}
                </option>
                {PERIOD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`common:academic.period_type.${type}`)}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>

          <Field
            label={t("common:academic.periods_per_cycle.label")}
            hint={t("academic.override.periods_hint")}
            error={periodsError}
          >
            {({ id, describedBy, invalid }) => (
              <SelectInput
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={
                  values.periods_per_cycle === null || Number.isNaN(values.periods_per_cycle)
                    ? ""
                    : String(values.periods_per_cycle)
                }
                onChange={(e) =>
                  onChange({
                    periods_per_cycle: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              >
                <option value="">{inheritLabel(parent?.periods_per_cycle)}</option>
                {PERIOD_COUNTS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>
        </div>

        <Field
          label={t("common:academic.grade_scale.label")}
          hint={t("academic.override.scale_hint")}
        >
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              aria-describedby={describedBy}
              value={values.grade_scale ?? ""}
              onChange={(e) =>
                onChange({ grade_scale: (e.target.value || null) as GradeScale | null })
              }
            >
              <option value="">
                {inheritLabel(parent && t(`common:academic.grade_scale.${parent.grade_scale}`))}
              </option>
              {GRADE_SCALES.map((scale) => (
                <option key={scale} value={scale}>
                  {t(`common:academic.grade_scale.${scale}`)}
                </option>
              ))}
            </SelectInput>
          )}
        </Field>
      </div>
    </details>
  )
}
